import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  LayoutDashboard, 
  UserPlus, 
  Briefcase, 
  FileText, 
  Settings,
  Menu,
  X,
  Shield,
  Activity,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", description: "Overview & metrics" },
  { to: "/onboard", icon: UserPlus, label: "New Client", description: "Client onboarding" },
  { to: "/cases", icon: Briefcase, label: "Cases", description: "Case management" },
  { to: "/reports", icon: FileText, label: "Reports", description: "Generate reports" },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex h-screen overflow-hidden bg-grid">
      {/* Mobile sidebar backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-72 glass-card transform transition-transform duration-300 lg:relative lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-4 px-6 py-6 border-b border-white/5">
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl blur-lg opacity-50" />
              <div className="relative p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
                <Shield className="h-6 w-6 text-white" />
              </div>
            </motion.div>
            <div>
              <h1 className="font-bold text-lg text-white tracking-tight">
                DueDiligence<span className="text-blue-400">AI</span>
              </h1>
              <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em]">Enterprise Platform</p>
            </div>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item, index) => (
              <motion.div
                key={item.to}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <NavLink
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => cn(
                    "sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group",
                    isActive 
                      ? "active text-white" 
                      : "text-slate-400 hover:text-white hover:bg-white/5"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-lg transition-all duration-200",
                    location.pathname === item.to 
                      ? "bg-gradient-to-br from-blue-500/20 to-purple-500/20" 
                      : "bg-white/5 group-hover:bg-white/10"
                  )}>
                    <item.icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p>{item.label}</p>
                    <p className="text-[10px] text-slate-500">{item.description}</p>
                  </div>
                  <ChevronRight className={cn(
                    "h-4 w-4 opacity-0 -translate-x-2 transition-all duration-200",
                    location.pathname === item.to && "opacity-100 translate-x-0"
                  )} />
                </NavLink>
              </motion.div>
            ))}
          </nav>

          {/* Bottom Section */}
          <div className="px-4 py-4 border-t border-white/5">
            <div className="glass-card rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <Activity className="h-4 w-4 text-green-400" />
                <span className="text-xs text-slate-400">System Status</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs text-green-400 font-medium">All Systems Operational</span>
              </div>
            </div>
            <button className="flex items-center gap-3 px-4 py-3 w-full text-slate-500 hover:text-slate-300 transition-colors rounded-lg hover:bg-white/5">
              <Settings className="h-4 w-4" />
              <span className="text-sm">Settings</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 glass-card border-b border-white/5">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden hover:bg-white/10"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <motion.h2 
                  key={location.pathname}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xl font-semibold text-white"
                >
                  {navItems.find(item => item.to === location.pathname)?.label || 'Dashboard'}
                </motion.h2>
                <p className="text-xs text-slate-500">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Risk Summary Badges */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2">
                {[
                  { count: 12, color: 'red', label: 'High' },
                  { count: 45, color: 'amber', label: 'Medium' },
                  { count: 128, color: 'green', label: 'Low' }
                ].map((item, index) => (
                  <motion.div
                    key={item.color}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-2 px-3 py-2 glass-card rounded-lg"
                  >
                    <div className={`risk-dot risk-dot-${item.color}`} />
                    <span className={cn(
                      "text-sm font-semibold font-mono-data",
                      item.color === 'red' && "text-red-400",
                      item.color === 'amber' && "text-amber-400",
                      item.color === 'green' && "text-emerald-400"
                    )} data-testid={`${item.color}-risk-count`}>
                      {item.count}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* User Avatar */}
              <motion.div 
                className="flex items-center gap-3 pl-3 border-l border-white/10"
                whileHover={{ scale: 1.02 }}
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-white">Admin</p>
                  <p className="text-[10px] text-slate-500">Compliance Officer</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center ring-2 ring-white/10">
                  <span className="text-sm font-bold text-white">AD</span>
                </div>
              </motion.div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
